<template>
  <config-base 
    class="opc-zmailcal-config" 
    @view-details="showPlanDetailDialog"
    :config-plan-tile-status="configPlanTileStatus">
    <template #config>
      <p class="license-config-title">{{ $t('billing.config.select_number_license') }}</p>
      <license-tip
        :type="planType"
        :number="license"
        :additionalSpans="additionalSpans"
        @click="handleLicenseTipsClick"
      />
      <input-number-with-popover
        ref="userInput"
        size="lg"
        controls-position="both"
        class="gap8"
        v-model="license"
        :min="minHosts"
        :max="maxHosts"
        :decrease-tips="decreaseTips"
        :label="`${title} select the number of hosts`"
        :dataTracking="{ buyInteraction: 'number of licenses', planName: trackingPlanName }"
        :data-testid="`checkout-input-license-${selectedPlan ? selectedPlan.plan_code : ''}`"
        @change="updatePlanLicense"
      />
      <bought-license :type="planType"/>
      <zm-banner show-close v-if="mx_downSellLicenseErrorMessage" type="info" class="gap16 opc-zmailcal-config__license-alert">
        {{ mx_downSellLicenseErrorMessage }}
      </zm-banner>
      <config-cycle
        v-model="selectedBillingCycle"
        :show-monthly-price-from-cycle="true"
        :unit="unit"
        :billing-cycle-list="billingCycleList"
        @switchCycleAction="updateBillingCycle"
      />
      <preview-payment class="gap24" />
    </template>
    <template #footer v-if="canRmv">
      <zm-button data-testid="checkout-button-126" type="link" ghost @click="removePlanByKey" :aria-label="`remove ${title} plan`">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
  <zoom-email-calendar-details :visible="showPlanDetails" @update:visible="showPlanDetails = $event" />
</template>

<script>
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import { get } from 'lodash';
import ConfigBase from '@/components/config-base.vue';
import ConfigCycle from '@/components/service/config-billing-cycle/index.vue';

import plansMixin from '@/plans/mx/plan';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';
import logo from '@/assets/plans/logo_zoom_email_calendar.svg';
import {
  planTypes,
  trackingPlanTypes,
  DEFAULT_MAX_HOST,
  DEFAULT_MIN_HOST,
} from '@/common/constants';
import { flowTypes, pageConfig } from '@/common/service/page-config.js';
import { isLastPlanTypeInPlantypeList } from '@/utils';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';
import LicenseTip from '@/plans/ZOOM_MEETING/license-tip.vue';
import autofill from "@/plans/mx/autofill";
import baseplanFreeToPaidReplenishMixin from "@/components/capacity/mx/freetrial-baseplan-replenish";
import AddonMixin from '@/plans/mx/addon';
import ZoomEmailCalendarDetails from '../dialog/details.vue';
import { planUnitMap } from '@/common/product-unit';

const ConfigBaseComp = {
  name: 'ZoomEmailCalendarConfigBase',
  extends: ConfigBase,
  data() {
    const title = this.$t('billing.zoom_email_calendar.title');
    const configDescription = this.$t('billing.zoom_email_calendar.desc');
    const planType = planTypes.ZOOM_EMAIL_CALENDAR;
    return {
      showLearnMore: false,
      unit: planUnitMap.user,
      isExpanded: true,
      orderByKey: 'amount',
      showPlanDetails: !this.$context.is.simplify,
      logo,
      title,
      planType,
      configDescription,
    };
  }
};

export default {
  name: 'Zoom-Email-Calendar-Config',
  key: planTypes[planTypes.ZOOM_EMAIL_CALENDAR],
  mixins: [
    plansMixin,
    AddonMixin,
    autofill,
    pageConfig.type === flowTypes.add2cart ? baseplanFreeToPaidReplenishMixin : null,
  ].filter(Boolean),
  components: { PreviewPayment, ConfigBase: ConfigBaseComp, InputNumberWithPopover, BoughtLicense, ConfigCycle, LicenseTip, ZoomEmailCalendarDetails },
  data() {
    return {
      planType: planTypes.ZOOM_EMAIL_CALENDAR,
      showPlanDetails: false,
      selectedPlanCode: '',
      selectedBillingCycle: '',
      unit: planUnitMap.user,
      license: 1,
      previousQuantity: 1,
      planTypes,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'getTypeGroupSelectedPlans', 'basicConfig', 'currentEditPlan']),
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
    }),
    selectedPlan() {
      return this.getTypeGroupSelectedPlans[this.planType]?.[0];
    },
    additionalSpans() {
      return {
        [planTypes.ZOOM_EMAIL_CALENDAR]: [
          {
            value: 1,
            default: true,
            text: '',
          },
          {
            value: this.maxLicenses,
            text: this.$t('billing.zone.max_of_plans_user', [this.maxLicenses, this.maxLicenses + 1]),
          },
        ],
      };
    },
    canRmv() {
      return this.$context.is.winback
        ? !isLastPlanTypeInPlantypeList(
            this.planType,
            this.getSelectedPlans.map((p) => p.type),
          )
        : this.isShowRemoveBtn;
    },
    trackingPlanName() {
      return get(trackingPlanTypes, planTypes.ZOOM_EMAIL_CALENDAR);
    },
    isEditFlow() {
      return this.$context.flow === flowTypes.edit && this.$context.is.editPage;
    },
    hasSelectedPlan() {
      return !!this.selectedPlanCode;
    },
    minHosts() {
      if (this.isEditFlow && (this.basicConfig.downSellData.disableDownSell || !this.isStandardPrice)) {
        return this.previousQuantity;
      }
      return DEFAULT_MIN_HOST;
    },
    maxHosts() {
      return DEFAULT_MAX_HOST;
    },
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },
    trackingPlanKey() {
      return get(trackingPlanTypes, get(planTypes, this.planType));
    },
    cartSelectedPlan() {
      return get(this.getSelectedPlans.filter((p) => p.type === this.planType), [0]);
    },
    decreaseTips() {
      const downSellData = this.basicConfig.downSellData;
      if (this.$context.is.edit && this.license === this.previousQuantity) {
        if (downSellData.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }

        return this.forNonStandardPriceTips;
      }

      return '';
    },
  },
  mounted() {
    this.previousQuantity = this.currentEditPlan?.cartInfo?.selected_number ?? DEFAULT_MIN_HOST;
    this.$nextTick(() => {
      this.mx_validDownSellLicense(this.license);
    });
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'removePlan']),
    ...mapActions(['switchBillingCycleAction']),
    showPlanDetailDialog() {
      this.showPlanDetails = true;
    },
    handleLicenseTipsClick(e) {
      if (e?.target?.tagName === 'A') {
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.trackingPlanName} - the maximum amount is ${this.maxHosts} users - contact sales`,
        );
      }
    },
    getFirstFocusableControl() {
      return Promise.resolve(this.$refs.userInput)
    },
    async updateBillingCycle(oldPlan, newPlan) {
      this.$tracking.buyInteraction(
        'billing cycle',
        `${this.trackingPlanName} - toggled billed ${newPlan.billing_cycle}`,
      );

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        { ...newPlan, cartInfo: { selected_number: this.license } },
      ]);
    },

    updatePlanLicense(newLicense) {
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: newLicense },
      });
      this.mx_validDownSellLicense(newLicense);
    },

  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan) {
        this.selectedPlanCode = plan?.plan_code ?? "";
        this.selectedBillingCycle = plan?.billing_cycle ?? "";
        this.license = plan?.cartInfo?.selected_number ?? this.minHosts;
      },
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/zoom-card';

$sub-font-color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
$main-font-color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

:deep(.gap8) {
  margin-top: 8px;
}
.gap16 {
  margin-top: 16px;
}
.gap24 {
  margin-top: 24px;
}

.opc-zmailcal-config {
  :deep {
    .opc-zm-base-card {
      &__name {
        text-transform: none;
      }
      &__main--desc__content {
        font-size: 16px;
        color: $sub-font-color;
      }
    }
  }
  .opc-base-alert {
    display: flex;
    align-items: center;
  }

  .license-config-title {
    font-size: 16px;
    font-weight: 600;
    color: $main-font-color;
    line-height: 20px;
  }
}
</style>
